import React from "react";
import "./DashboardSetup.scss";

const steps = [
  {
    key: "inspectionCycles",
    title: "Create an inspection cycle",
    detail: "Set the period and standards for your inspection work.",
    icon: "bx-calendar",
    createLabel: "Create Inspection Cycle",
    viewLabel: "View Inspection Cycles",
    createPath: "/inspections/new",
    viewPath: "/inspections/list",
  },
  {
    key: "engagements",
    title: "Add an engagement",
    detail: "Add the client engagement that will be inspected.",
    icon: "bx-briefcase",
    createLabel: "Create Engagement",
    viewLabel: "View Engagements",
    createPath: "/engagements/new",
    viewPath: "/engagements/list",
  },
  {
    key: "inspections",
    title: "Create an inspection review",
    detail: "Configure reviews in an inspection cycle and open assigned reviews from My Work.",
    icon: "bx-check-square",
    createLabel: "Open Inspection Cycles",
    viewLabel: "Open My Work",
    createPath: "/inspections/list",
    viewPath: "/review-tasks",
  },
];

export default function DashboardSetup({
  counts,
  summary,
  error,
  onRetry,
  onNavigate,
  onLaunch,
}) {
  if (error)
    return (
      <section
        className="dashboard-setup dashboard-setup--message"
        role="alert"
      >
        <i className="bx bx-error-circle" aria-hidden="true" />
        <h1 className="piers-page-title">Dashboard is unavailable</h1>
        <p>{error}</p>
        <button type="button" onClick={onRetry}>
          Retry
        </button>
      </section>
    );

  if (summary)
    return (
      <section
        className="dashboard-setup"
        aria-labelledby="dashboard-setup-title"
      >
        <header className="dashboard-setup__header">
          <div>
            <span>WELCOME TO PIERS</span>
            <h1 className="piers-page-title" id="dashboard-setup-title">Your work, one step at a time</h1>
            <p>
              Open your assigned work and complete the responsibilities
              available to your role.
            </p>
          </div>
          <strong>
            {summary.completedCount} of {summary.totalCount} complete
          </strong>
        </header>
        <div
          className="dashboard-setup__progress"
          role="progressbar"
          aria-label="Work progress"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={summary.percentComplete}
        >
          <i style={{ width: `${summary.percentComplete}%` }} />
        </div>
        <div className="dashboard-setup__steps">
          {summary.milestones.map((step, index) => {
            const isComplete = step.status === "completed";
            return (
              <article key={step.id} className={`is-${step.status}`}>
                <div className="dashboard-setup__step-icon">
                  <i
                    className={`bx ${isComplete ? "bx-check" : "bx-briefcase"}`}
                    aria-hidden="true"
                  />
                </div>
                <div className="dashboard-setup__step-copy">
                  <small>
                    STEP {index + 1} ·{" "}
                    {isComplete
                      ? "COMPLETE"
                      : step.status === "current"
                      ? "UP NEXT"
                      : "TO DO"}
                  </small>
                  <h2>{step.title}</h2>
                  <p>{step.detail || step.description}</p>
                </div>
                {step.action && (
                  <button
                    type="button"
                    onClick={() => onLaunch(step.action, step)}
                  >
                    {step.action.label}
                    <i className="bx bx-right-arrow-alt" aria-hidden="true" />
                  </button>
                )}
              </article>
            );
          })}
        </div>
      </section>
    );

  if (!counts)
    return (
      <section
        className="dashboard-setup dashboard-setup--message"
        role="status"
      >
        <i className="bx bx-loader-alt bx-spin" aria-hidden="true" />
        <p>Checking your inspection workspace…</p>
      </section>
    );

  const complete = steps.filter((step) => Number(counts[step.key]) > 0).length;
  return (
    <section
      className="dashboard-setup"
      aria-labelledby="dashboard-setup-title"
    >
      <header className="dashboard-setup__header">
        <div>
          <span>GETTING STARTED</span>
          <h1 className="piers-page-title" id="dashboard-setup-title">Set up your inspection workspace</h1>
          <p>
            Complete these steps to start seeing inspection activity on your
            dashboard.
          </p>
        </div>
        <strong>
          {complete} of {steps.length} complete
        </strong>
      </header>
      <div
        className="dashboard-setup__progress"
        role="progressbar"
        aria-label="Inspection setup progress"
        aria-valuemin="0"
        aria-valuemax={steps.length}
        aria-valuenow={complete}
      >
        <i style={{ width: `${(complete / steps.length) * 100}%` }} />
      </div>
      <div className="dashboard-setup__steps">
        {steps.map((step, index) => {
          const isComplete = Number(counts[step.key]) > 0;
          return (
            <article key={step.key} className={isComplete ? "is-complete" : ""}>
              <div className="dashboard-setup__step-icon">
                <i
                  className={`bx ${isComplete ? "bx-check" : step.icon}`}
                  aria-hidden="true"
                />
              </div>
              <div className="dashboard-setup__step-copy">
                <small>
                  STEP {index + 1} · {isComplete ? "COMPLETE" : "TO DO"}
                </small>
                <h2>{step.title}</h2>
                <p>{step.detail}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  onNavigate(isComplete ? step.viewPath : step.createPath)
                }
              >
                {isComplete ? step.viewLabel : step.createLabel}
                <i className="bx bx-right-arrow-alt" aria-hidden="true" />
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
